import React from 'react';
import { 
  AlertTriangle, 
  CheckCircle, 
  Info, 
  X, 
  AlertCircle,
  BellRing,
  ArrowRight,
  ExternalLink,
  ChevronRight
} from 'lucide-react';
import Button from '../form-control/Button';
import { GrAction } from 'react-icons/gr';
import PopOnMe from '../elements/poponme';

export type BannerVariant = 'info' | 'success' | 'warning' | 'error' | 'neutral' | 'promo';
export type BannerPosition = 'top' | 'bottom';

export interface BannerProps {
  message: string;
  variant?: BannerVariant;
  position?: BannerPosition;
  icon?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'outline';
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'outline';
  };
  actions?: React.ReactNode;
  isSticky?: boolean;
  hasStripe?: boolean;
}

const getBannerIcon = (variant: BannerVariant) => {
  switch (variant) {
    case 'info':
      return <Info className="h-5 w-5 text-blue-500" />;
    case 'success':
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    case 'warning':
      return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    case 'error':
      return <AlertCircle className="h-5 w-5 text-red-500" />;
    case 'neutral':
      return <BellRing className="h-5 w-5 text-gray-500" />;
    case 'promo':
      return <ExternalLink className="h-5 w-5 text-indigo-500" />;
    default:
      return null;
  }
};

const variantStyles = {
  info: {
    background: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    stripe: 'bg-blue-500',
  },
  success: {
    background: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    stripe: 'bg-green-500',
  },
  warning: {
    background: 'bg-yellow-50',
    border: 'border-yellow-200',
    text: 'text-yellow-800',
    stripe: 'bg-yellow-500',
  },
  error: {
    background: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    stripe: 'bg-red-500',
  },
  neutral: {
    background: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-800',
    stripe: 'bg-gray-500',
  },
  promo: {
    background: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-800',
    stripe: 'bg-indigo-500',
  }
};

/**
 * Banner Component
 * 
 * A full-width banner component for site-wide notifications, announcements, and promotions.
 * Can be positioned at the top or bottom of the page, and supports custom actions.
 */
export const Banner: React.FC<BannerProps> = ({
  message,
  variant = 'info',
  position = 'top',
  icon,
  dismissible = true,
  onDismiss,
  className = '',
  action,
  actions,
  secondaryAction,
  isSticky = false,
  hasStripe = false,
}) => {
  const variantStyle = variantStyles[variant];
  
  return (
    <div
      className={`
        w-full 
        ${variantStyle.background}
        border-y ${variantStyle.border}
        ${position === 'bottom' ? 'border-b-0' : 'border-t-0'}
        ${isSticky ? `sticky ${position === 'top' ? 'top-0' : 'bottom-0'}` : ''}
        ${className}
      `}
      role="alert"
    >
      {hasStripe && (
        <div className={`h-1 ${variantStyle.stripe}`}></div>
      )}
      
      <div className="container mx-auto px-4 py-3 sm:flex sm:items-center sm:justify-between">
        <div className="flex items-center">
          {(icon || getBannerIcon(variant)) && (
            <div className="flex-shrink-0 mr-3">
              {icon || getBannerIcon(variant)}
            </div>
          )}
          
          <div className={`${variantStyle.text} text-sm font-medium`}>
            {message}
          </div>
        </div>
        
        <div className="mt-2 sm:mt-0 sm:ml-4 flex flex-shrink-0 items-center space-x-2">
          {action && (
            <Button
              label={action.label}
              size="sm"
              onClick={action.onClick}
              appendIcon={<ChevronRight size={14} />}
              bgColor='primary'
              className='!h-7 !text-xs'
            />
          )}

          {actions && (<>{actions}</>)
          }

          {secondaryAction && (
            <Button
              label={secondaryAction.label}
              size="sm"
              onClick={secondaryAction.onClick}
              bgColor='primary'
            />
          )}
          
          {dismissible && onDismiss && (
            <button
              type="button"
              className="inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={onDismiss}
              aria-label="Dismiss"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Banner;
